// Shared jsPDF setup and drawing helpers used by both the PDI report
// (PDFExport) and the blank printable checklists (PrintableChecklist).

export const SITE_URL = 'www.car-pdi.app';

export const PDF_COLORS = {
  primary: [34, 197, 94],
  primaryLight: [220, 252, 231],
  danger: [239, 68, 68],
  dangerLight: [254, 226, 226],
  warning: [245, 158, 11],
  warningLight: [254, 243, 199],
  dark: [15, 23, 42],
  medium: [71, 85, 105],
  light: [148, 163, 184],
  veryLight: [241, 245, 249],
  pass: [22, 163, 74],
  white: [255, 255, 255],
  critical: [220, 38, 38],
  criticalLight: [254, 242, 242],
  headerBg: [15, 23, 42],
  sectionBg: [248, 250, 252],
  tipBg: [240, 253, 244],
};

export const statusColor = (status) => {
  switch (status) {
    case 'pass': return PDF_COLORS.pass;
    case 'fail': return PDF_COLORS.danger;
    case 'attention': return PDF_COLORS.warning;
    default: return PDF_COLORS.light;
  }
};

export const statusBg = (status) => {
  switch (status) {
    case 'pass': return PDF_COLORS.primaryLight;
    case 'fail': return PDF_COLORS.dangerLight;
    case 'attention': return PDF_COLORS.warningLight;
    default: return PDF_COLORS.veryLight;
  }
};

export const statusText = (status) => {
  switch (status) {
    case 'pass': return 'PASSED';
    case 'fail': return 'FAILED';
    case 'attention': return 'ATTENTION';
    default: return 'PENDING';
  }
};

/**
 * Creates an A4 portrait jsPDF document with a mutable cursor (y, pageNum)
 * and the drawing helpers shared by all generators.
 *
 * Options:
 * - margin: page margin in mm
 * - footerLeft: optional text drawn at the bottom-left of each page
 */
export const createPdf = async ({ margin = 12, footerLeft = '' } = {}) => {
  const { default: jsPDF } = await import('jspdf');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const pdf = {
    doc,
    pageWidth,
    pageHeight,
    margin,
    contentWidth: pageWidth - 2 * margin,
    y: margin,
    pageNum: 1,

    rect(x, y, w, h, r, color) {
      doc.setFillColor(...color);
      doc.roundedRect(x, y, w, h, r, r, 'F');
    },

    strokeRect(x, y, w, h, r, color, width = 0.5) {
      doc.setDrawColor(...color);
      doc.setLineWidth(width);
      doc.roundedRect(x, y, w, h, r, r, 'S');
    },

    checkbox(x, y, size = 3, label = '') {
      doc.setDrawColor(100, 100, 100);
      doc.setLineWidth(0.3);
      doc.rect(x, y, size, size, 'S');
      if (label) {
        doc.setFontSize(6);
        doc.setTextColor(...PDF_COLORS.medium);
        doc.text(label, x + size + 1, y + 3);
      }
    },

    footer() {
      doc.setFontSize(7);
      doc.setTextColor(...PDF_COLORS.light);
      doc.text(`Page ${pdf.pageNum}`, pageWidth / 2, pageHeight - 6, { align: 'center' });
      if (footerLeft) {
        doc.text(footerLeft, margin, pageHeight - 6);
      }
      doc.setTextColor(...PDF_COLORS.primary);
      doc.text(SITE_URL, pageWidth - margin, pageHeight - 6, { align: 'right' });
    },

    // Footer on the current page, then a fresh page with the cursor reset.
    addPage() {
      pdf.footer();
      doc.addPage();
      pdf.pageNum++;
      pdf.y = margin;
    },

    newPageIfNeeded(requiredSpace = 20) {
      if (pdf.y + requiredSpace > pageHeight - 15) {
        pdf.addPage();
        return true;
      }
      return false;
    },

    // Draws a photo thumbnail; falls back to a placeholder on bad data.
    photo(data, x, y, size) {
      try {
        if (data && data.startsWith('data:image')) {
          const format = data.includes('image/png') ? 'PNG' : 'JPEG';
          doc.addImage(data, format, x, y, size, size);
          doc.setDrawColor(...PDF_COLORS.light);
          doc.setLineWidth(0.3);
          doc.rect(x, y, size, size, 'S');
        } else {
          throw new Error('Invalid image data');
        }
      } catch {
        pdf.rect(x, y, size, size, 1, PDF_COLORS.veryLight);
        doc.setFontSize(6);
        doc.setTextColor(...PDF_COLORS.light);
        doc.text('Photo', x + size / 2, y + size / 2, { align: 'center' });
      }
    },
  };

  return pdf;
};
